import React, { useCallback, useEffect } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  faEdit,
  faTrash,
  faEye,
  faUndo,
  faCheckCircle,
  faTimesCircle,
  faFileExcel,
} from '@fortawesome/free-solid-svg-icons'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import ListHeader from '../../../components/ListHeader'
import useAction from '../../../hooks/useAction'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import FilterCollapse from '../../../components/FilterCollapse'
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import { inputsFiltersBills } from './filters/bill'
import { formatDateFront } from '../../../utils/formatDate'
import { maskCurrency } from '../../../utils/maskUtils'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { billStatusColorMap } from '../../../utils/statusColorMap'
import { BillingEnum, getBillingLabel } from '../../../enums/billing-enum'
import { exportSnapshotToExcel } from '../../../utils/exportSnapshotToExcel'
import { Chip } from '@mui/material'
import { organizationPrimaryColors } from '../../../layouts/PermanentSidebarMUI'

const BillPage = () => {
  const { canAccess } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      number: '',
      'createdBy.name': '',
      'organization.alias': '',
      deleted_at: false,
    },
    'billings'
  )

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters)

  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        number: '',
        'createdBy.name': '',
        'organization.alias': '',
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  const {
    get: baseServiceGet,
    put: baseServicePut,
    del: baseServiceDelete,
  } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  const {
    data: billsData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQueryWithLoader({
    queryKey: ['billings', filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters)
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
      }
      const response = await baseServiceGet(entities.billing.get, params)
      return response.result
    },
    staleTime: 60 * 1000,
    select: (data) => {
      return {
        bills: data?.data?.map((bill) => ({
          id: bill?.id,
          number: bill?.number ?? '-',
          created_by: bill?.created_by?.name,
          organization: bill?.organization?.alias,
          customer: bill?.customer?.name,
          billing_date: formatDateFront({ rawDate: bill?.created_at }),
          start_date: formatDateFront({ rawDate: bill?.start_date }),
          end_date: formatDateFront({ rawDate: bill?.end_date }),
          amount: maskCurrency(bill?.amount),
          status: bill?.status || '-',
          isDeleted: !!bill?.deleted_at,
          spreadsheet_snapshot: bill?.spreadsheet_snapshot,
          items:
            bill.items?.map((item) => ({
              id: item?.id,
              alias: item?.alias,
              description: item?.description,
              quantity: item?.quantity,
              price: Number(item.price),
            })) || [],
        })),
        totalPages: data.last_page,
      }
    },
    placeholderData: (prev) => prev,
  })

  const { bills = [], totalPages = 1 } = billsData || {}

  const columns = [
    { key: 'id', label: 'N° Faturamento' },
    { key: 'number', label: 'N° Fiscal' },
    { key: 'created_by', label: 'Criado por' },
    {
      key: 'organization',
      label: 'Organização',
      render: (value, bill) => {
        const organizationName = value?.toUpperCase()

        const primaryColor =
          organizationPrimaryColors[organizationName] ||
          organizationPrimaryColors.ALUCOM

        return (
          <Chip
            label={value}
            size="small"
            variant="filled"
            sx={{
              backgroundColor: primaryColor,
              color: '#fff',
              fontWeight: 'bold',
            }}
          />
        )
      },
    },
    { key: 'customer', label: 'Cliente' },
    { key: 'start_date', label: 'Data de Inicio' },
    { key: 'end_date', label: 'Data de Fim' },
    {
      key: 'status',
      label: 'Status',
      render: (value) => getBillingLabel(value),
    },
    {
      key: 'amount',
      label: 'Valor',
      numeric: true,
      isMoney: true,
      align: 'right',
    },
  ]

  const collapseConfig = {
    key: ['items'],
    columns: [
      [
        { key: 'items.alias', label: 'Item' },
        { key: 'items.description', label: 'Descrição' },
        { key: 'items.quantity', label: 'Quantidade' },
        {
          key: 'items.price',
          label: 'Preço',
          numeric: true,
          isMoney: true,
          align: 'right',
        },
        {
          key: 'total',
          label: 'Total',
          numeric: true,
          isMoney: true,
          align: 'right',
          // Calcula o total da linha
          render: (value, row) => {
            const rowTotal =
              (Number(row.quantity) || 0) * (Number(row.price) || 0)
            return new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(rowTotal)
          },
          // Função para o cálculo do total geral no rodapé
          totalCalc: (row) =>
            (Number(row.quantity) || 0) * (Number(row.price) || 0),
        },
      ],
    ],
  }

  useEffect(() => {
    isLoading ? showLoader() : hideLoader()
  }, [isLoading, showLoader, hideLoader])

  useEffect(() => {
    if (isError) {
      showNotification(
        'error',
        `Erro ao carregar faturamentos: ${
          error.response?.data?.message || error.message
        }`
      )
    }
  }, [isError, error, showNotification])

  const {
    openModalConfirmation,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    action,
  } = useAction(navigate)

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const inactivateMutation = useMutation({
    mutationFn: async (id) => {
      showLoader()
      return await baseServiceDelete(entities.billing.delete(id))
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        queryClient.invalidateQueries([
          'billings',
          transformFiltersForUrl(filters),
          pagination,
        ])
      }
    },
    onError: (err) => {
      console.error('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  const activateMutation = inactivateMutation

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const response = await baseServicePut(entities.billing.updateStatus(id), {
        billing: { status },
      })
      return response
    },
    onSuccess: (response) => {
      if (response.status === 200) {
        queryClient.invalidateQueries(['billings'])
      }
    },
    onError: (error) => {
      showNotification(
        'error',
        error.response?.data?.message || 'Erro ao atualizar status.'
      )
    },
  })

  const handleExportExcel = useCallback(
    (billing) => {
      try {
        showLoader()

        const metadata = {
          id: billing.id,
          number: billing.number,
          created_by: billing.created_by,
          organization: billing.organization,
          customer: billing.customer,
          start_date: billing.start_date,
          end_date: billing.end_date,
          status: getBillingLabel(billing.status),
          amount: billing.amount,
        }

        const normalize = (value) =>
          String(value || '')
            .trim()
            .toUpperCase()
            .replace(/\s+/g, '_')
            // eslint-disable-next-line no-useless-escape
            .replace(/[^\w\-]/g, '_')

        const customer = normalize(billing.customer)
        const organization = normalize(billing.organization)

        const startDate = normalize(billing.start_date)
        const endDate = normalize(billing.end_date)

        const fileName = `FATURAMENTO_${billing.id}_${billing.number || 'SEM_NOTA'}_${startDate}_${endDate}_${customer} - ${organization}`

        exportSnapshotToExcel(billing.spreadsheet_snapshot, fileName, {
          includeFormulas: true,
          includeStyles: true,
          metadata,
          metadataSheetName: 'Resumo',
          metadataRows: [
            ['Informações do Faturamento'],
            [],
            ['Campo', 'Valor'],
          ],
          labelMap: {
            number: 'N° Fiscal',
            created_by: 'Criado por',
            organization: 'Organização',
            customer: 'Cliente',
            start_date: 'Data de Início',
            end_date: 'Data de Fim',
            status: 'Status',
            amount: 'Valor Total',
            bill_type: 'Tipo de Faturamento',
            id: 'ID do Faturamento',
          },
        })

        showNotification('success', 'Planilha exportada com sucesso!')
      } catch (error) {
        showNotification('error', 'Erro ao exportar planilha: ' + error.message)
        console.error(error)
      } finally {
        hideLoader()
      }
    },
    [showLoader, showNotification, hideLoader]
  )

  const actions = [
    {
      id: 'exportExcel',
      icon: faFileExcel,
      title: 'Exportar Excel',
      buttonClass: 'btn-success',
      permission: 'Ver faturamentos',
      onClick: (billing) => handleExportExcel(billing),
      show: (billing) => !billing.isDeleted && canAccess('Ver faturamentos'),
    },
    {
      id: 'approve',
      icon: faCheckCircle,
      title: 'Aprovar',
      buttonClass: 'btn-success',
      permission: 'Aprovar faturamentos',
      onClick: (billing) =>
        showConfirmation(
          billing,
          'Deseja aprovar este faturamento?',
          async () =>
            await updateStatusMutation.mutateAsync({
              id: billing.id,
              status: BillingEnum.APPROVED,
            })
        ),
      show: (billing) =>
        !billing.isDeleted &&
        billing.status === BillingEnum.PENDING &&
        canAccess('Aprovar faturamentos'),
    },
    {
      id: 'cancel',
      icon: faTimesCircle,
      title: 'Cancelar',
      buttonClass: 'btn-warning',
      permission: 'Cancelar faturamentos',
      onClick: (billing) =>
        showConfirmation(
          billing,
          'Deseja cancelar este faturamento?',
          async () =>
            await updateStatusMutation.mutateAsync({
              id: billing.id,
              status: BillingEnum.CANCELLED,
            })
        ),
      show: (billing) =>
        !billing.isDeleted &&
        billing.status === BillingEnum.PENDING &&
        canAccess('Cancelar faturamentos'),
    },
    {
      id: 'details',
      icon: faEye,
      title: 'Ver Detalhes',
      buttonClass: 'btn-info',
      permission: 'Ver faturamentos',
      onClick: (billing) => navigate(`/faturamento/${billing.id}/detalhes`),
      show: (billing) => !billing.isDeleted && canAccess('Ver faturamentos'),
    },
    {
      id: 'edit',
      icon: faEdit,
      title: 'Editar',
      buttonClass: 'btn-primary',
      permission: 'Atualizar faturamentos',
      onClick: (billing) => navigate(`/faturamento/${billing.id}/editar`),
      show: (billing) =>
        !billing.isDeleted &&
        [BillingEnum.PENDING, BillingEnum.DRAFT].includes(billing.status) &&
        canAccess('Atualizar faturamentos'),
    },
    {
      id: 'delete',
      icon: faTrash,
      title: 'Excluir',
      buttonClass: 'btn-danger',
      permission: 'Excluir faturamentos',
      onClick: (billing) =>
        showConfirmation(
          billing,
          `Você tem certeza que deseja excluir?`,
          async () => {
            try {
              await inactivateMutation.mutateAsync(billing.id)
            } catch (error) {
              console.log('Erro ao excluir faturamento:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (billing) =>
        !billing.isDeleted &&
        [BillingEnum.PENDING, BillingEnum.DRAFT].includes(billing.status) &&
        canAccess('Excluir faturamentos'),
    },
    {
      id: 'activate',
      icon: faUndo,
      title: 'Restaurar',
      buttonClass: 'btn-info',
      permission: 'Excluir faturamentos',
      onClick: (billing) =>
        showConfirmation(
          billing,
          `Você tem certeza que deseja restaurar?`,
          async () => {
            try {
              await activateMutation.mutateAsync(billing.id)
            } catch (error) {
              console.log('Erro ao restaurar faturamento:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (billing) => billing.isDeleted && canAccess('Excluir faturamentos'),
    },
  ]

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Faturamento" showBackButton backUrl="/dashboard" />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsFiltersBills({
            localFilterValues,
            handleFilterChange,
          })}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Faturamentos"
          buttonText="Cadastrar Faturamento"
          buttonLink="/faturamento/criar"
          permission={'Criar faturamentos'}
          canAccess={canAccess}
        />

        <TableWithInactiveFilter
          data={bills}
          columns={columns}
          collapseConfig={collapseConfig}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="collapse"
          quantitySpaces={-1}
          rowColorConfig={{
            colorMap: billStatusColorMap,
            propertyKey: 'status',
          }}
          isLoading={isLoading}
          isFetching={isFetching}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={
            `N° do Movimento (${selectedItem?.number}) - Cliente: ${selectedItem?.customer}` ||
            ''
          }
          text={action?.text}
        />
      </div>
    </MainLayout>
  )
}

export default BillPage
